import { useState, useMemo } from "react";
import { ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import CustomTooltip from "../shared/CustomTooltip";
import {
  INVESTMENTS, PORTFOLIO_CHART_DATA, COMPARISON_CHART_DATA, COMPARE_META, fmt,
} from "../../data/mockData";
import { useIsMobile } from "../../hooks/useIsMobile";

const RANGES     = ["1D", "1W", "1M", "6M", "YTD", "1Y", "5Y", "MAX"];
const barColors  = ["#D4AF37", "#52D68A", "#B8D4E8", "#D4B8E8"];

// ─── Investment Type Configuration ────────────────────────────────────────────
const TYPE_CONFIG = {
  stock: {
    label: "Stock", fullLabel: "Stocks (Equities)",
    description: "Company shares, high growth potential",
    color: "#52D68A", emoji: "📈",
    fields: [
      { key: "ticker",       label: "Ticker Symbol",        type: "text",   required: true,  placeholder: "AAPL",                  half: true },
      { key: "name",         label: "Company Name",         type: "text",   required: true,  placeholder: "Apple Inc.",            half: true },
      { key: "shares",       label: "Shares",               type: "number", required: true,  placeholder: "10",                    half: true },
      { key: "avgPrice",     label: "Avg Buy Price ($)",    type: "number", required: true,  placeholder: "150.00",                half: true },
      { key: "currentPrice", label: "Current Price ($)",    type: "number", required: true,  placeholder: "175.00",                half: true },
      { key: "sector",       label: "Sector",               type: "select", required: false,
        options: ["Technology","Healthcare","Finance","Consumer","Energy","Materials","Utilities","Real Estate","Industrials","Communication"],
        half: true },
      { key: "exchange",     label: "Exchange",             type: "select", required: false,
        options: ["NYSE","NASDAQ","AMEX","OTC"], half: true },
      { key: "notes",        label: "Notes",                type: "text",   required: false, placeholder: "Optional notes...",     half: true },
    ],
  },
  bond: {
    label: "Bond", fullLabel: "Bonds (Fixed Income)",
    description: "Fixed interest, lower risk",
    color: "#B8D4E8", emoji: "📜",
    fields: [
      { key: "name",         label: "Bond Name",            type: "text",   required: true,  placeholder: "US Treasury 10Y",       half: true },
      { key: "issuer",       label: "Issuer",               type: "text",   required: true,  placeholder: "U.S. Treasury",         half: true },
      { key: "faceValue",    label: "Face Value ($)",       type: "number", required: true,  placeholder: "10000",                 half: true },
      { key: "currentValue", label: "Current Value ($)",    type: "number", required: true,  placeholder: "10250",                 half: true },
      { key: "couponRate",   label: "Coupon Rate (%)",      type: "number", required: true,  placeholder: "4.5",                   half: true },
      { key: "maturityDate", label: "Maturity Date",        type: "date",   required: true,                                        half: true },
      { key: "ytm",          label: "Yield to Maturity (%)", type: "number", required: false, placeholder: "4.2",                 half: true },
      { key: "creditRating", label: "Credit Rating",        type: "select", required: false,
        options: ["AAA","AA+","AA","AA-","A+","A","A-","BBB+","BBB","BBB-","BB","B","CCC","D"],
        half: true },
    ],
  },
  mutualfund: {
    label: "Mutual Fund", fullLabel: "Mutual Funds",
    description: "Professionally managed portfolio",
    color: "#D4B8E8", emoji: "🏦",
    fields: [
      { key: "name",         label: "Fund Name",            type: "text",   required: true,  placeholder: "Fidelity 500 Index Fund", half: false },
      { key: "symbol",       label: "Fund Symbol",          type: "text",   required: false, placeholder: "FXAIX",                 half: true },
      { key: "manager",      label: "Fund Manager",         type: "text",   required: false, placeholder: "Fidelity Investments",  half: true },
      { key: "units",        label: "Units",                type: "number", required: true,  placeholder: "25.5",                  half: true },
      { key: "nav",          label: "NAV per Unit ($)",     type: "number", required: true,  placeholder: "185.00",                half: true },
      { key: "costBasis",    label: "Avg Cost / Unit ($)",  type: "number", required: false, placeholder: "170.00",                half: true },
      { key: "expenseRatio", label: "Expense Ratio (%)",    type: "number", required: false, placeholder: "0.015",                 half: true },
      { key: "category",     label: "Category",             type: "select", required: false,
        options: ["Large Cap Blend","Large Cap Growth","Large Cap Value","Mid Cap","Small Cap","International","Bond","Balanced","Money Market","Sector"],
        half: false },
    ],
  },
  etf: {
    label: "ETF", fullLabel: "Exchange-Traded Funds (ETFs)",
    description: "Like mutual funds, trades like a stock",
    color: "#C8E8B8", emoji: "📊",
    fields: [
      { key: "ticker",       label: "Ticker Symbol",        type: "text",   required: true,  placeholder: "SPY",                   half: true },
      { key: "name",         label: "ETF Name",             type: "text",   required: true,  placeholder: "SPDR S&P 500 ETF",      half: true },
      { key: "shares",       label: "Shares",               type: "number", required: true,  placeholder: "15",                    half: true },
      { key: "avgPrice",     label: "Avg Buy Price ($)",    type: "number", required: true,  placeholder: "420.00",                half: true },
      { key: "currentPrice", label: "Current Price ($)",    type: "number", required: true,  placeholder: "455.00",                half: true },
      { key: "expenseRatio", label: "Expense Ratio (%)",    type: "number", required: false, placeholder: "0.09",                  half: true },
      { key: "indexTracked", label: "Index Tracked",        type: "text",   required: false, placeholder: "S&P 500",               half: true },
      { key: "category",     label: "Category",             type: "select", required: false,
        options: ["Equity","Bond","Commodity","Real Estate","International","Sector","Thematic","Leveraged"],
        half: true },
    ],
  },
  cash: {
    label: "Cash", fullLabel: "Cash Equivalents",
    description: "Low-risk, short-term instruments",
    color: "var(--text-primary)", emoji: "💵",
    fields: [
      { key: "name",         label: "Account Name",         type: "text",   required: true,  placeholder: "High-Yield Savings",    half: true },
      { key: "institution",  label: "Institution",          type: "text",   required: true,  placeholder: "Marcus by Goldman",     half: true },
      { key: "cashType",     label: "Instrument Type",      type: "select", required: true,
        options: ["Savings Account","Money Market","CD","T-Bill","T-Note","I-Bond","Other"],
        half: true },
      { key: "amount",       label: "Amount ($)",           type: "number", required: true,  placeholder: "25000",                 half: true },
      { key: "interestRate", label: "Interest Rate (%)",    type: "number", required: false, placeholder: "4.75",                  half: true },
      { key: "maturityDate", label: "Maturity Date",        type: "date",   required: false, note: "Leave blank if no fixed maturity", half: true },
    ],
  },
  realestate: {
    label: "Real Estate", fullLabel: "Real Estate",
    description: "Property or REIT investments",
    color: "#E8C87A", emoji: "🏠",
    fields: [
      { key: "name",          label: "Property / REIT Name",       type: "text",   required: true,  placeholder: "123 Main St or VNQ",  half: true },
      { key: "reType",        label: "Type",                        type: "select", required: true,
        options: ["Residential","Commercial","Industrial","Vacation Rental","REIT","Land"],
        half: true },
      { key: "purchasePrice", label: "Purchase Price ($)",          type: "number", required: true,  placeholder: "400000",              half: true },
      { key: "currentValue",  label: "Current Market Value ($)",    type: "number", required: true,  placeholder: "480000",              half: true },
      { key: "annualIncome",  label: "Annual Rental Income ($)",    type: "number", required: false, placeholder: "24000",               half: true },
      { key: "mortgage",      label: "Outstanding Mortgage ($)",    type: "number", required: false, placeholder: "300000",              half: true },
      { key: "location",      label: "City, State",                 type: "text",   required: false, placeholder: "Austin, TX",          half: false },
    ],
  },
  alternative: {
    label: "Alternative", fullLabel: "Alternatives",
    description: "Crypto, commodities, collectibles",
    color: "#F4B8B8", emoji: "💎",
    fields: [
      { key: "name",         label: "Asset Name",           type: "text",   required: true,  placeholder: "Bitcoin",               half: true },
      { key: "altType",      label: "Type",                 type: "select", required: true,
        options: ["Cryptocurrency","Precious Metal","Energy Commodity","Agricultural Commodity","Collectible","Private Equity","Other"],
        half: true },
      { key: "quantity",     label: "Quantity",             type: "number", required: true,  placeholder: "0.5",                   half: true },
      { key: "unit",         label: "Unit",                 type: "text",   required: false, placeholder: "BTC / oz / barrel",     half: true },
      { key: "avgPrice",     label: "Avg Buy Price ($)",    type: "number", required: true,  placeholder: "42000",                 half: true },
      { key: "currentPrice", label: "Current Price ($)",    type: "number", required: true,  placeholder: "65000",                 half: true },
      { key: "custody",      label: "Custodian / Location", type: "text",  required: false, placeholder: "Coinbase / Safe Vault", half: false },
    ],
  },
};

// ─── Helper: compute current value from a manual investment ───────────────────
const computeInvValue = (inv) => {
  const n = (k) => parseFloat(inv[k] || 0);
  switch (inv.type) {
    case "stock":      case "etf":        return n("shares")   * n("currentPrice");
    case "bond":                          return n("currentValue");
    case "mutualfund":                    return n("units")    * n("nav");
    case "cash":                          return n("amount");
    case "realestate":                    return n("currentValue");
    case "alternative":                   return n("quantity") * n("currentPrice");
    default: return 0;
  }
};

// ─── Helper: compute gain/loss ─────────────────────────────────────────────────
const computeInvGain = (inv) => {
  const n   = (k) => parseFloat(inv[k] || 0);
  const val = computeInvValue(inv);
  let cost  = 0;
  switch (inv.type) {
    case "stock": case "etf":  cost = n("shares")   * n("avgPrice"); break;
    case "bond":               cost = n("faceValue"); break;
    case "mutualfund":         cost = n("units")    * (n("costBasis") || n("nav")); break;
    case "cash":               return null;
    case "realestate":         cost = n("purchasePrice"); break;
    case "alternative":        cost = n("quantity") * n("avgPrice"); break;
    default: return null;
  }
  if (!cost) return null;
  return { amt: val - cost, pct: ((val - cost) / cost) * 100 };
};

// ─── Helper: type-specific details line ───────────────────────────────────────
const getInvDetails = (inv) => {
  const n = (k) => parseFloat(inv[k] || 0);
  switch (inv.type) {
    case "stock": case "etf":
      return `${inv.shares} shares · $${n("currentPrice").toFixed(2)}`;
    case "bond":
      return `${inv.couponRate || "—"}% coupon · matures ${inv.maturityDate ? new Date(inv.maturityDate).getFullYear() : "—"}`;
    case "mutualfund":
      return `${inv.units} units · NAV $${n("nav").toFixed(2)}`;
    case "cash":
      return [inv.cashType, inv.interestRate ? `${inv.interestRate}% APY` : null].filter(Boolean).join(" · ");
    case "realestate":
      return [inv.reType, inv.location].filter(Boolean).join(" · ");
    case "alternative":
      return `${inv.quantity} ${inv.unit || ""} · $${n("currentPrice").toLocaleString()}/unit`.trim();
    default: return "—";
  }
};

// ─── Comparison tooltip ────────────────────────────────────────────────────────
const CompTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "10px", padding: "12px 16px" }}>
      <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginBottom: "8px" }}>{label}</div>
      {payload.map((p, i) => p.value != null && (
        <div key={i} style={{ fontSize: "12px", color: p.color, fontFamily: "'JetBrains Mono', monospace", marginBottom: "2px" }}>
          {p.name}: {p.value >= 0 ? "+" : ""}{p.value.toFixed(2)}%
        </div>
      ))}
    </div>
  );
};

// ─── Add Investment Modal ──────────────────────────────────────────────────────
function AddInvestmentModal({ onClose, onAdd }) {
  const [step, setStep]         = useState(1);
  const [selType, setSelType]   = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors]     = useState({});

  const config = selType ? TYPE_CONFIG[selType] : null;

  const handleTypeSelect = (type) => { setSelType(type); setFormData({}); setErrors({}); setStep(2); };
  const handleChange     = (key, val) => {
    setFormData(p => ({ ...p, [key]: val }));
    if (errors[key]) setErrors(p => ({ ...p, [key]: null }));
  };

  const validate = () => {
    const errs = {};
    config.fields.forEach(f => { if (f.required && !formData[f.key]) errs[f.key] = "Required"; });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onAdd({ type: selType, id: Date.now(), ...formData });
    onClose();
  };

  const inputStyle = (key) => ({
    width: "100%", background: "var(--bg-main)",
    border: `1px solid ${errors[key] ? "#e07070" : "var(--border-strong)"}`,
    borderRadius: "8px", padding: "9px 12px",
    fontSize: "13px", color: "var(--text-primary)",
    fontFamily: "'JetBrains Mono', monospace", outline: "none",
    boxSizing: "border-box", transition: "border-color 0.15s",
  });

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)",
        zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
      }}
    >
      <div style={{
        background: "var(--bg-surface)", border: "1px solid var(--border-strong)",
        borderRadius: "20px", width: "100%", maxWidth: "620px",
        maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 24px 80px rgba(0,0,0,0.85)",
      }}>
        {/* Modal Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "24px 28px 20px", borderBottom: "1px solid var(--border)", position: "sticky", top: 0,
          background: "var(--bg-surface)", zIndex: 1, borderRadius: "20px 20px 0 0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {step === 2 && (
              <button
                onClick={() => { setStep(1); setSelType(null); }}
                style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: "20px", lineHeight: 1, padding: "0 6px 0 0" }}
              >←</button>
            )}
            <div>
              <div style={{ fontSize: "16px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)" }}>
                {step === 1 ? "Add Investment" : `Add ${config?.label}`}
              </div>
              {step === 2 && (
                <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", marginTop: "2px" }}>
                  {config?.fullLabel} · fill in the details below
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "var(--bg-elevated)", border: "none", borderRadius: "8px",
              width: "32px", height: "32px", color: "var(--text-muted)", cursor: "pointer",
              fontSize: "18px", display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >×</button>
        </div>

        <div style={{ padding: "24px 28px 28px" }}>
          {/* ── Step 1: Type cards ─────────────────────────────────── */}
          {step === 1 && (
            <div>
              <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "16px" }}>
                Select investment type
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
                {Object.entries(TYPE_CONFIG).map(([key, tc]) => (
                  <button
                    key={key}
                    onClick={() => handleTypeSelect(key)}
                    style={{
                      background: "var(--bg-main)", border: "1px solid var(--border)",
                      borderRadius: "14px", padding: "16px 12px",
                      cursor: "pointer", textAlign: "left", transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--bg-card)"; e.currentTarget.style.borderColor = tc.color + "60"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "var(--bg-main)"; e.currentTarget.style.borderColor = "var(--border)"; }}
                  >
                    <div style={{ fontSize: "22px", marginBottom: "8px" }}>{tc.emoji}</div>
                    <div style={{ fontSize: "12px", fontWeight: "600", color: tc.color, fontFamily: "'JetBrains Mono', monospace", marginBottom: "4px" }}>{tc.label}</div>
                    <div style={{ fontSize: "10px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", lineHeight: "1.4" }}>{tc.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 2: Form ───────────────────────────────────────── */}
          {step === 2 && config && (
            <div>
              {/* Type badge */}
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                background: config.color + "14", border: `1px solid ${config.color}30`,
                borderRadius: "8px", padding: "6px 12px", marginBottom: "20px",
              }}>
                <span style={{ fontSize: "16px" }}>{config.emoji}</span>
                <span style={{ fontSize: "12px", color: config.color, fontFamily: "'JetBrains Mono', monospace", fontWeight: "600" }}>{config.fullLabel}</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                {config.fields.map(field => (
                  <div key={field.key} style={{ gridColumn: field.half ? "span 1" : "span 2" }}>
                    <label style={{
                      fontSize: "10px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace",
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      marginBottom: "5px", display: "block",
                    }}>
                      {field.label}
                      {field.required && <span style={{ color: "#e07070", marginLeft: "3px" }}>*</span>}
                    </label>

                    {field.type === "select" ? (
                      <select
                        value={formData[field.key] || ""}
                        onChange={e => handleChange(field.key, e.target.value)}
                        style={{ ...inputStyle(field.key), appearance: "none", cursor: "pointer" }}
                      >
                        <option value="">Select…</option>
                        {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        placeholder={field.placeholder || ""}
                        value={formData[field.key] || ""}
                        onChange={e => handleChange(field.key, e.target.value)}
                        style={inputStyle(field.key)}
                        onFocus={e => { e.target.style.borderColor = config.color + "80"; }}
                        onBlur={e => { e.target.style.borderColor = errors[field.key] ? "#e07070" : "var(--border-strong)"; }}
                      />
                    )}

                    {errors[field.key] && (
                      <div style={{ fontSize: "10px", color: "#e07070", fontFamily: "'JetBrains Mono', monospace", marginTop: "4px" }}>
                        {errors[field.key]}
                      </div>
                    )}
                    {field.note && !errors[field.key] && (
                      <div style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginTop: "4px" }}>{field.note}</div>
                    )}
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <div style={{
                display: "flex", justifyContent: "flex-end", gap: "10px",
                marginTop: "28px", paddingTop: "20px", borderTop: "1px solid var(--border)",
              }}>
                <button
                  onClick={onClose}
                  style={{
                    background: "transparent", border: "1px solid var(--border-strong)",
                    borderRadius: "10px", padding: "10px 20px",
                    fontSize: "12px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", cursor: "pointer",
                  }}
                >Cancel</button>
                <button
                  onClick={handleSubmit}
                  style={{
                    background: config.color + "18", border: `1px solid ${config.color}40`,
                    borderRadius: "10px", padding: "10px 24px",
                    fontSize: "12px", color: config.color,
                    fontFamily: "'JetBrains Mono', monospace", cursor: "pointer", fontWeight: "600",
                  }}
                >Add to Holdings</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function InvestmentsPage() {
  const isMobile = useIsMobile();
  const [range, setRange]             = useState("6M");
  const [compareWith, setCompareWith] = useState(["SPY"]);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  // Persist manual investments to localStorage
  const [manualInvestments, setManualInvestments] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ledger_manual_investments") || "[]"); }
    catch { return []; }
  });

  const saveManual = (updated) => {
    setManualInvestments(updated);
    localStorage.setItem("ledger_manual_investments", JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent("manualInvestmentsChanged"));
  };
  const addInvestment    = (inv) => saveManual([...manualInvestments, inv]);
  const removeInvestment = (id)  => saveManual(manualInvestments.filter(m => m.id !== id));

  const stockTotal    = INVESTMENTS.reduce((s, inv) => s + inv.value, 0);
  const manualTotal   = manualInvestments.reduce((s, m) => s + computeInvValue(m), 0);
  const grandTotal    = stockTotal + manualTotal;

  const stockGainAmt  = INVESTMENTS.reduce((s, inv) => s + inv.gainAmt, 0);
  const manualGainAmt = manualInvestments.reduce((s, m) => { const g = computeInvGain(m); return s + (g ? g.amt : 0); }, 0);
  const totalGainAmt  = stockGainAmt + manualGainAmt;
  const costBasis     = grandTotal - totalGainAmt;
  const totalGainPct  = costBasis > 0 ? (totalGainAmt / costBasis) * 100 : 0;

  const rawChartData  = PORTFOLIO_CHART_DATA[range];
  const chartStart    = rawChartData[0].value;
  const chartEnd      = rawChartData[rawChartData.length - 1].value;
  const chartGain     = chartEnd - chartStart;
  const chartGainPct  = ((chartGain / chartStart) * 100).toFixed(2);
  const chartPositive = chartGain >= 0;
  const isComparison  = compareWith.length > 0;

  const displayData = useMemo(() => {
    const portData = PORTFOLIO_CHART_DATA[range];
    const portBase = portData[0].value;
    return portData.map((d, i) => {
      const pt = {
        label:     d.label,
        Portfolio: isComparison ? parseFloat(((d.value / portBase - 1) * 100).toFixed(2)) : d.value,
      };
      if (isComparison) {
        compareWith.forEach(id => {
          const secVals = COMPARISON_CHART_DATA[id]?.[range];
          if (secVals && i < secVals.length)
            pt[id] = parseFloat(((secVals[i] / secVals[0] - 1) * 100).toFixed(2));
        });
      }
      return pt;
    });
  }, [range, compareWith, isComparison]);

  const spyGainPct = useMemo(() => {
    const v = COMPARISON_CHART_DATA.SPY?.[range];
    return v ? ((v[v.length - 1] / v[0] - 1) * 100).toFixed(2) : null;
  }, [range]);

  return (
    <div>
      {/* ── Summary Cards ─────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: "16px", marginBottom: "24px" }}>
        {[
          { label: "Portfolio Value", value: fmt(grandTotal),                                                                    sub: manualTotal > 0 ? `${fmt(manualTotal)} manual` : "+$4,440.50 today",  subColor: "#52D68A" },
          { label: "Total Gain",      value: (totalGainAmt >= 0 ? "+" : "") + fmt(totalGainAmt),                                 sub: (totalGainPct >= 0 ? "+" : "") + totalGainPct.toFixed(1) + "% all time", subColor: totalGainAmt >= 0 ? "#52D68A" : "#e07070" },
          { label: "Today's Change",  value: "+$1,240.50",                                                                       sub: "+0.69%",            subColor: "#52D68A" },
          { label: "Dividend Income", value: "$842.00",                                                                          sub: "YTD 2026",          subColor: "#D4AF37" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "14px", padding: "20px" }}>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px" }}>{s.label}</div>
            <div style={{ fontSize: "22px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "700", color: "var(--text-primary)" }}>{s.value}</div>
            <div style={{ fontSize: "12px", color: s.subColor, fontFamily: "'JetBrains Mono', monospace", marginTop: "4px" }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 360px", gap: "20px" }}>

        {/* ── Holdings Table ─────────────────────────────────────── */}
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "24px" }}>

          {/* Section header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>Holdings</div>
            <div style={{ fontSize: "12px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>
              Total: <span style={{ color: "#D4AF37" }}>{fmt(grandTotal)}</span>
            </div>
          </div>

          {/* Stocks sub-header */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <span style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase" }}>Stocks / ETFs</span>
            <div style={{ flex: 1, height: "1px", background: "var(--bg-hover)" }} />
          </div>

          {/* Stock table header — desktop only */}
          {!isMobile && (
            <div style={{
              display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr 1.2fr",
              gap: "12px", padding: "8px 0", borderBottom: "1px solid var(--border)", marginBottom: "8px",
            }}>
              {["Security", "Shares", "Avg Price", "Price", "Value", "Gain/Loss", "% of Portfolio"].map(h => (
                <div key={h} style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>{h}</div>
              ))}
            </div>
          )}

          {/* Stock rows — table on desktop, cards on mobile */}
          {INVESTMENTS.map((inv, i) => {
            const pct      = (inv.value / grandTotal) * 100;
            const barColor = barColors[i % barColors.length];
            const aboveAvg = inv.price >= inv.avgPrice;

            if (isMobile) {
              return (
                <div key={i} style={{ padding: "12px 0", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
                    <div>
                      <div style={{ fontSize: "14px", color: "var(--text-primary)", fontWeight: "500" }}>{inv.ticker}</div>
                      <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "1px" }}>{inv.name}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: "14px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)" }}>{fmt(inv.value)}</div>
                      <div style={{ fontSize: "12px", color: "#52D68A", fontFamily: "'JetBrains Mono', monospace", marginTop: "1px" }}>+{inv.gain}%</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
                    <span style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>{inv.shares} shares · ${inv.price.toFixed(2)}</span>
                    <span style={{ fontSize: "11px", color: barColor, fontFamily: "'JetBrains Mono', monospace" }}>{pct.toFixed(1)}% of portfolio</span>
                  </div>
                  <div style={{ height: "3px", background: "var(--bg-hover)", borderRadius: "2px", marginTop: "6px" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: "2px" }} />
                  </div>
                </div>
              );
            }

            return (
              <div key={i} style={{
                display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr 1.2fr",
                gap: "12px", padding: "14px 0", borderBottom: "1px solid var(--border)", alignItems: "center",
              }}>
                <div>
                  <div style={{ fontSize: "14px", color: "var(--text-primary)", fontWeight: "500" }}>{inv.ticker}</div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>{inv.name}</div>
                </div>
                <div style={{ fontSize: "13px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>{inv.shares}</div>
                <div>
                  <div style={{ fontSize: "13px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>${inv.avgPrice.toFixed(2)}</div>
                  <div style={{ fontSize: "10px", color: aboveAvg ? "#52D68A" : "#e07070", fontFamily: "'JetBrains Mono', monospace", marginTop: "2px" }}>
                    {aboveAvg ? "▲" : "▼"} ${Math.abs(inv.price - inv.avgPrice).toFixed(2)}
                  </div>
                </div>
                <div style={{ fontSize: "13px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>${inv.price.toFixed(2)}</div>
                <div style={{ fontSize: "14px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)" }}>{fmt(inv.value)}</div>
                <div>
                  <div style={{ fontSize: "13px", color: "#52D68A", fontFamily: "'JetBrains Mono', monospace" }}>+{inv.gain}%</div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>+{fmt(inv.gainAmt)}</div>
                </div>
                <div>
                  <div style={{ marginBottom: "5px" }}>
                    <span style={{ fontSize: "13px", fontFamily: "'JetBrains Mono', monospace", color: barColor, fontWeight: "500" }}>{pct.toFixed(1)}%</span>
                  </div>
                  <div style={{ height: "4px", background: "var(--bg-hover)", borderRadius: "2px", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: "2px", transition: "width 0.8s cubic-bezier(0.4,0,0.2,1)" }} />
                  </div>
                </div>
              </div>
            );
          })}

          {/* ── Manual / Additional Holdings ──────────────────────── */}
          {manualInvestments.length > 0 && (
            <div style={{ marginTop: "20px" }}>
              {/* Group by type */}
              {Object.entries(
                manualInvestments.reduce((groups, inv) => {
                  if (!groups[inv.type]) groups[inv.type] = [];
                  groups[inv.type].push(inv);
                  return groups;
                }, {})
              ).map(([type, items]) => {
                const tc = TYPE_CONFIG[type];
                return (
                  <div key={type} style={{ marginBottom: "20px" }}>
                    {/* Type group header */}
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "16px 0 10px" }}>
                      <span style={{ fontSize: "14px" }}>{tc.emoji}</span>
                      <span style={{ fontSize: "10px", color: tc.color, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase" }}>{tc.fullLabel}</span>
                      <div style={{ flex: 1, height: "1px", background: tc.color + "30" }} />
                    </div>

                    {/* Type-specific table header */}
                    <div style={{
                      display: "grid", gridTemplateColumns: "2.2fr 1.8fr 1fr 1fr 1fr 0.8fr",
                      gap: "10px", padding: "6px 0", borderBottom: `1px solid ${tc.color}20`, marginBottom: "4px",
                    }}>
                      {["Asset", "Details", "Cost Basis", "Value", "Return", "% Portfolio"].map(h => (
                        <div key={h} style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase" }}>{h}</div>
                      ))}
                    </div>

                    {/* Rows */}
                    {items.map((inv) => {
                      const value   = computeInvValue(inv);
                      const gain    = computeInvGain(inv);
                      const details = getInvDetails(inv);
                      const pct     = grandTotal > 0 ? (value / grandTotal) * 100 : 0;
                      const costAmt = (() => {
                        const n = (k) => parseFloat(inv[k] || 0);
                        switch (inv.type) {
                          case "stock": case "etf": return n("shares") * n("avgPrice");
                          case "bond":              return n("faceValue");
                          case "mutualfund":        return n("units") * (n("costBasis") || n("nav"));
                          case "realestate":        return n("purchasePrice");
                          case "alternative":       return n("quantity") * n("avgPrice");
                          default: return null;
                        }
                      })();

                      return (
                        <div key={inv.id} style={{
                          display: "grid", gridTemplateColumns: "2.2fr 1.8fr 1fr 1fr 1fr 0.8fr",
                          gap: "10px", padding: "12px 0",
                          borderBottom: "1px solid var(--border)", alignItems: "center",
                        }}>
                          {/* Asset */}
                          <div>
                            <div style={{ fontSize: "13px", color: "var(--text-primary)", fontWeight: "500" }}>
                              {inv.ticker || inv.symbol || inv.name}
                            </div>
                            {(inv.name && (inv.ticker || inv.symbol)) && (
                              <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>{inv.name}</div>
                            )}
                          </div>

                          {/* Details */}
                          <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", lineHeight: "1.5" }}>
                            {details || "—"}
                          </div>

                          {/* Cost Basis */}
                          <div style={{ fontSize: "12px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
                            {costAmt != null ? fmt(costAmt) : "—"}
                          </div>

                          {/* Current Value */}
                          <div style={{ fontSize: "13px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: "var(--text-primary)" }}>
                            {fmt(value)}
                          </div>

                          {/* Return */}
                          <div>
                            {gain ? (
                              <>
                                <div style={{ fontSize: "12px", color: gain.amt >= 0 ? "#52D68A" : "#e07070", fontFamily: "'JetBrains Mono', monospace" }}>
                                  {gain.amt >= 0 ? "+" : ""}{gain.pct.toFixed(1)}%
                                </div>
                                <div style={{ fontSize: "10px", color: "var(--text-muted)", marginTop: "2px" }}>
                                  {gain.amt >= 0 ? "+" : ""}{fmt(gain.amt)}
                                </div>
                              </>
                            ) : (
                              <div style={{ fontSize: "12px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
                                {inv.type === "cash" && inv.interestRate ? `${inv.interestRate}% APY` : "—"}
                              </div>
                            )}
                          </div>

                          {/* % Portfolio + remove */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span style={{ fontSize: "12px", fontFamily: "'JetBrains Mono', monospace", color: tc.color }}>{pct.toFixed(1)}%</span>
                            <button
                              onClick={() => removeInvestment(inv.id)}
                              title="Remove"
                              style={{ background: "none", border: "none", color: "var(--text-faint)", cursor: "pointer", fontSize: "14px", padding: "0", lineHeight: 1 }}
                              onMouseEnter={e => e.currentTarget.style.color = "#e07070"}
                              onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
                            >🗑</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Add Investment Button ─────────────────────────────── */}
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              marginTop: manualInvestments.length > 0 ? "8px" : "16px",
              width: "100%", background: "transparent",
              border: "1px dashed var(--border-strong)", borderRadius: "10px",
              padding: "12px", fontSize: "12px", color: "var(--text-muted)",
              fontFamily: "'JetBrains Mono', monospace", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: "8px", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--border-active)"; e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.background = "var(--bg-main)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border-strong)"; e.currentTarget.style.color = "var(--text-faint)"; e.currentTarget.style.background = "transparent"; }}
          >
            <span style={{ fontSize: "16px", lineHeight: 1 }}>+</span>
            Add Investment
          </button>
        </div>

        {/* ── Portfolio Growth Chart ─────────────────────────────── */}
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "16px", padding: "24px" }}>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Portfolio Growth
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "13px", fontFamily: "'Capsule Sans', sans-serif", fontWeight: "600", color: chartPositive ? "#52D68A" : "#e07070" }}>
                {chartPositive ? "+" : ""}{fmt(chartGain)}
              </div>
              <div style={{ fontSize: "11px", fontFamily: "'JetBrains Mono', monospace", color: chartPositive ? "#52D68A" : "#e07070", marginTop: "2px" }}>
                {chartPositive ? "+" : ""}{chartGainPct}% · {range}
              </div>
              {isComparison && spyGainPct !== null && (
                <div style={{ fontSize: "10px", color: "#F4B8B8", fontFamily: "'JetBrains Mono', monospace", marginTop: "2px" }}>
                  S&P 500: {Number(spyGainPct) >= 0 ? "+" : ""}{spyGainPct}%
                </div>
              )}
            </div>
          </div>

          {/* Range selector */}
          <div style={{
            display: "flex", gap: "2px", marginBottom: "12px",
            background: "var(--bg-main)", borderRadius: "8px", padding: "3px", border: "1px solid var(--border)",
          }}>
            {RANGES.map(r => (
              <button
                key={r}
                onClick={() => setRange(r)}
                style={{
                  flex: 1, padding: "5px 0",
                  background: range === r ? "var(--bg-hover)" : "transparent",
                  border: range === r ? "1px solid var(--border-active)" : "1px solid transparent",
                  borderRadius: "6px",
                  fontSize: "10px", fontFamily: "'JetBrains Mono', monospace",
                  color: range === r ? "#D4AF37" : "#555",
                  cursor: "pointer", transition: "all 0.15s",
                  fontWeight: range === r ? "600" : "normal",
                }}
              >{r}</button>
            ))}
          </div>

          {/* Compare chips */}
          <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap", marginBottom: "14px" }}>
            <span style={{ fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase", marginRight: "2px" }}>
              Compare:
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "4px", background: "var(--bg-card)", borderRadius: "6px", padding: "3px 8px", border: "1px solid var(--border-strong)" }}>
              <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#52D68A" }} />
              <span style={{ fontSize: "10px", color: "#52D68A", fontFamily: "'JetBrains Mono', monospace" }}>Portfolio</span>
            </div>
            {compareWith.map(id => {
              const meta = COMPARE_META.find(m => m.id === id);
              if (!meta) return null;
              return (
                <div key={id} style={{ display: "flex", alignItems: "center", gap: "4px", background: "var(--bg-card)", borderRadius: "6px", padding: "3px 8px", border: `1px solid ${meta.color}50` }}>
                  <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: meta.color }} />
                  <span style={{ fontSize: "10px", color: meta.color, fontFamily: "'JetBrains Mono', monospace" }}>{meta.label}</span>
                  <button
                    onClick={() => setCompareWith(prev => prev.filter(x => x !== id))}
                    style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: "0 0 0 2px", fontSize: "13px", lineHeight: 1, display: "flex", alignItems: "center" }}
                  >×</button>
                </div>
              );
            })}
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setShowAddMenu(p => !p)}
                style={{ background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "6px", padding: "3px 8px", fontSize: "10px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", cursor: "pointer" }}
              >+ Add</button>
              {showAddMenu && (
                <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "8px", padding: "6px", zIndex: 100, minWidth: "155px", boxShadow: "0 4px 20px rgba(0,0,0,0.6)" }}>
                  {COMPARE_META.filter(m => !compareWith.includes(m.id)).map(opt => (
                    <div
                      key={opt.id}
                      onClick={() => { setCompareWith(prev => [...prev, opt.id]); setShowAddMenu(false); }}
                      style={{ display: "flex", alignItems: "center", gap: "8px", padding: "7px 8px", cursor: "pointer", borderRadius: "6px", transition: "background 0.1s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--bg-hover)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: opt.color, flexShrink: 0 }} />
                      <span style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace" }}>{opt.label}</span>
                    </div>
                  ))}
                  {COMPARE_META.every(m => compareWith.includes(m.id)) && (
                    <div style={{ fontSize: "10px", color: "var(--text-muted)", padding: "7px 8px", fontFamily: "'JetBrains Mono', monospace" }}>All added</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Chart */}
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={displayData}>
              <defs>
                <linearGradient id="invGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#52D68A" stopOpacity={isComparison ? 0.12 : 0.3} />
                  <stop offset="95%" stopColor="#52D68A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" tick={{ fill: "var(--text-faint)", fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis
                hide={!isComparison} domain={["auto", "auto"]}
                tick={{ fill: "var(--text-faint)", fontSize: 9, fontFamily: "'JetBrains Mono', monospace" }}
                axisLine={false} tickLine={false}
                tickFormatter={v => `${v >= 0 ? "+" : ""}${v.toFixed(0)}%`} width={46}
              />
              <Tooltip content={isComparison ? <CompTooltip /> : <CustomTooltip />} />
              <Area type="monotone" dataKey="Portfolio" name="Portfolio" stroke="#52D68A" strokeWidth={2} fill="url(#invGrad)" dot={false} activeDot={{ r: 4, fill: "#52D68A" }} />
              {isComparison && compareWith.map(id => {
                const meta = COMPARE_META.find(m => m.id === id);
                if (!meta) return null;
                return (
                  <Line key={id} type="monotone" dataKey={id} name={meta.label} stroke={meta.color} strokeWidth={1.5} strokeDasharray="5 3" dot={false} activeDot={{ r: 3, fill: meta.color }} />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>

          {isComparison && (
            <div style={{ marginTop: "8px", fontSize: "9px", color: "var(--text-faint)", fontFamily: "'JetBrains Mono', monospace" }}>
              % return from start of period · all series indexed to 0%
            </div>
          )}

          {/* Allocation */}
          <div style={{ marginTop: "20px" }}>
            <div style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "12px" }}>Allocation</div>
            {[
              { name: "Vanguard IRA", pct: 71, color: "#D4AF37" },
              { name: "Robinhood",    pct: 29, color: "#52D68A" },
            ].map(a => (
              <div key={a.name} style={{ marginBottom: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                  <span style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{a.name}</span>
                  <span style={{ fontSize: "12px", fontFamily: "'JetBrains Mono', monospace", color: "var(--text-secondary)" }}>{a.pct}%</span>
                </div>
                <div style={{ height: "5px", background: "var(--bg-hover)", borderRadius: "3px" }}>
                  <div style={{ height: "100%", width: `${a.pct}%`, background: a.color, borderRadius: "3px" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Add Investment Modal ───────────────────────────────────── */}
      {showAddModal && (
        <AddInvestmentModal
          onClose={() => setShowAddModal(false)}
          onAdd={addInvestment}
        />
      )}
    </div>
  );
}
